import type {
  AlertDto,
  ApiErrorDto,
  ClientStatusDto,
  CreateRuleDefinitionDto,
  CreateRuleTargetDto,
  DiscoveredMetricDto,
  LoginRequestDto,
  LoginResponseDto,
  MetricDto,
  OverviewDto,
  RuleDefinitionDto,
  RuleTargetDto,
  SeriesDto,
  SettingsDto,
  UpdateRuleDefinitionDto,
  UpdateRuleTargetDto,
  UpdateSettingsDto,
} from '@citilight-monitor/contracts';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export interface Range {
  from: string;
  to?: string;
}

/** Typed access to the citilight-monitor API. */
export class ApiClient {
  constructor(
    private readonly getToken: () => string | null,
    private readonly onUnauthorized: () => void,
  ) {}

  login(body: LoginRequestDto): Promise<LoginResponseDto> {
    return this.request('/api/auth/login', { method: 'POST', body: JSON.stringify(body) }, false);
  }

  overview(): Promise<OverviewDto> {
    return this.request('/api/clients');
  }

  client(id: string): Promise<ClientStatusDto> {
    return this.request(`/api/clients/${encodeURIComponent(id)}`);
  }

  series(id: string, range: Range): Promise<SeriesDto> {
    return this.request(`/api/clients/${encodeURIComponent(id)}/series?${toQuery(range)}`);
  }

  alerts(id: string, range: Range): Promise<AlertDto[]> {
    return this.request(`/api/clients/${encodeURIComponent(id)}/alerts?${toQuery(range)}`);
  }

  ruleTargets(): Promise<RuleTargetDto[]> {
    return this.request('/api/rule-targets');
  }

  createRuleTarget(body: CreateRuleTargetDto): Promise<RuleTargetDto> {
    return this.request('/api/rule-targets', { method: 'POST', body: JSON.stringify(body) });
  }

  updateRuleTarget(id: string, body: UpdateRuleTargetDto): Promise<RuleTargetDto> {
    return this.request(`/api/rule-targets/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  deleteRuleTarget(id: string): Promise<void> {
    return this.request(`/api/rule-targets/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  storedMetrics(ruleTargetId: string): Promise<MetricDto[]> {
    return this.request(`/api/rule-targets/${encodeURIComponent(ruleTargetId)}/metrics`);
  }

  discoverMetrics(ruleTargetId: string): Promise<DiscoveredMetricDto[]> {
    return this.request(`/api/rule-targets/${encodeURIComponent(ruleTargetId)}/metrics/discover`);
  }

  updateMetricLabel(ruleTargetId: string, metricId: string, label: string): Promise<MetricDto> {
    return this.request(`/api/rule-targets/${encodeURIComponent(ruleTargetId)}/metrics/${encodeURIComponent(metricId)}`, {
      method: 'PUT',
      body: JSON.stringify({ label }),
    });
  }

  rules(ruleTargetId?: string): Promise<RuleDefinitionDto[]> {
    const query = ruleTargetId ? `?ruleTargetId=${encodeURIComponent(ruleTargetId)}` : '';
    return this.request(`/api/rules${query}`);
  }

  createRule(body: CreateRuleDefinitionDto): Promise<RuleDefinitionDto> {
    return this.request('/api/rules', { method: 'POST', body: JSON.stringify(body) });
  }

  updateRule(id: string, body: UpdateRuleDefinitionDto): Promise<RuleDefinitionDto> {
    return this.request(`/api/rules/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  deleteRule(id: string): Promise<void> {
    return this.request(`/api/rules/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  settings(): Promise<SettingsDto> {
    return this.request('/api/settings');
  }

  updateSettings(body: UpdateSettingsDto): Promise<SettingsDto> {
    return this.request('/api/settings', { method: 'PUT', body: JSON.stringify(body) });
  }

  private async request<T>(path: string, init: RequestInit = {}, authenticated = true): Promise<T> {
    const headers = new Headers(init.headers);
    if (init.body) headers.set('Content-Type', 'application/json');
    const token = this.getToken();
    if (authenticated && token) headers.set('Authorization', `Bearer ${token}`);

    const response = await fetch(path, { ...init, headers });
    if (response.status === 401 && authenticated) this.onUnauthorized();
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as ApiErrorDto | null;
      throw new ApiError(response.status, body?.error ?? `Request failed (${response.status})`);
    }
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }
}

function toQuery(range: Range): string {
  const params = new URLSearchParams({ from: range.from });
  if (range.to) params.set('to', range.to);
  return params.toString();
}
